/**
 * AnalystMarkdown
 * Classification: custom
 *
 * Renders an AI-written investigation summary, with the entity values inside it
 * turned into interactive chips an analyst can copy and pivot on.
 *
 * Published as `@aistrike-dev/ui/markdown`, deliberately NOT re-exported from
 * `src/index.ts`: `react-markdown` and its unified/remark tree are a large
 * dependency and a consumer that never renders model output should never pay
 * for one. `scripts/assert-no-markdown-in-root.ts` enforces that at build time.
 *
 * ### Safety
 *
 * The input is model output, so it is treated as untrusted. `react-markdown`
 * builds the tree itself and never parses raw HTML unless `rehype-raw` is added
 * -- which is why it is not added here -- so markup in the source renders as
 * text rather than as elements. It also sanitises URLs to safe protocols, which
 * stops a generated `javascript:` link. Both properties depend on nothing being
 * bolted on: adding `rehype-raw` to this component would make it an XSS sink.
 */
import { useMemo, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Chip, type EntityKind, type EntityVerdict } from '@/atoms/Chip';
import {
  EntityMenu,
  type Entity,
  type EntityMenuAction,
} from '@/molecules/EntityMenu';
import { fontFamily } from '@/tokens/typography';
import { ENTITY_ELEMENT, remarkEntitySpans, type EntitySpan } from './entitySpans';

export interface AnalystMarkdownProps {
  /** The raw markdown. Entity offsets are resolved against this string. */
  source: string;
  /**
   * Resolved entity spans, as offsets into `source`. Supplied by the model or
   * the enrichment service -- never derived by pattern-matching the prose.
   */
  entities?: readonly EntitySpan[];
  /**
   * Renders one marker on the block declaring it AI generated. Broad visibility
   * in Carbon's terms: one marker for the whole generated block, never one per
   * entity chip, which would be decoration and would collide with verdict tint.
   */
  aiGenerated?: boolean;
  /** Cap on marked spans per block. Defaults to 8. */
  maxMarkedPerBlock?: number;
  /** Pivot actions appended to every entity menu, after the copy actions. */
  entityActions?: EntityMenuAction[];
  onEntityAction?: (actionId: string, entity: Entity) => void;
}

interface OpenEntity {
  anchorEl: HTMLElement;
  entity: Entity;
}

/** Props react-markdown passes down for the custom entity element. */
interface EntityElementProps {
  'data-entity-kind'?: string;
  'data-entity-value'?: string;
  'data-entity-display'?: string;
  'data-entity-verdict'?: string;
}

/**
 * @example
 * <AnalystMarkdown
 *   source={summary.markdown}
 *   entities={summary.entities}
 *   aiGenerated
 *   entityActions={[{ id: 'pivot', label: 'Pivot to search' }]}
 *   onEntityAction={handleEntityAction}
 * />
 */
export function AnalystMarkdown({
  source,
  entities = [],
  aiGenerated = false,
  maxMarkedPerBlock,
  entityActions,
  onEntityAction,
}: AnalystMarkdownProps) {
  const [open, setOpen] = useState<OpenEntity | null>(null);

  const remarkPlugins = useMemo(
    () => [remarkGfm, [remarkEntitySpans, { spans: entities, maxPerBlock: maxMarkedPerBlock }]],
    [entities, maxMarkedPerBlock],
  );

  const components = useMemo<Components>(() => {
    /**
     * The rendered element carries only strings, so enrichment attributes are
     * looked up from the span list by canonical value rather than serialised
     * into DOM attributes.
     */
    const attributesFor = (value: string) =>
      entities.find((span) => span.value === value)?.attributes;

    const EntityElement = ({
      'data-entity-kind': kind,
      'data-entity-value': value,
      'data-entity-display': display,
      'data-entity-verdict': verdict,
    }: EntityElementProps) => {
      if (!kind || !value) return null;
      const entity: Entity = {
        kind: kind as EntityKind,
        value,
        display,
        verdict: verdict as EntityVerdict | undefined,
        attributes: attributesFor(value),
      };
      return (
        <Chip
          purpose="entity"
          kind={entity.kind}
          value={entity.value}
          display={entity.display}
          verdict={entity.verdict}
          onOpen={(event) => setOpen({ anchorEl: event.currentTarget, entity })}
        />
      );
    };

    const standard: Components = {
      p: ({ children }) => (
        <Typography variant="body1" sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
          {children}
        </Typography>
      ),
      h1: ({ children }) => <Heading level="h5">{children}</Heading>,
      h2: ({ children }) => <Heading level="h6">{children}</Heading>,
      h3: ({ children }) => <Heading level="subtitle1">{children}</Heading>,
      h4: ({ children }) => <Heading level="subtitle2">{children}</Heading>,
      h5: ({ children }) => <Heading level="subtitle2">{children}</Heading>,
      h6: ({ children }) => <Heading level="subtitle2">{children}</Heading>,

      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 3, my: 1.5, display: 'grid', gap: 0.5 }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 3, my: 1.5, display: 'grid', gap: 0.5 }}>
          {children}
        </Box>
      ),
      li: ({ children }) => (
        <Typography component="li" variant="body1">
          {children}
        </Typography>
      ),

      a: ({ href, children }) => (
        // Model output is untrusted, so an outbound link never gets access to
        // this window via `opener`.
        <Link href={href} target="_blank" rel="noopener noreferrer nofollow">
          {children}
        </Link>
      ),

      code: ({ children }) => (
        <Box
          component="code"
          sx={{
            fontFamily: fontFamily.mono,
            fontSize: '0.875em',
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {children}
        </Box>
      ),
      pre: ({ children }) => (
        <Box
          component="pre"
          sx={{
            fontFamily: fontFamily.mono,
            fontSize: '0.8125rem',
            p: 1.5,
            my: 1.5,
            overflowX: 'auto',
            borderRadius: 1,
            bgcolor: 'action.hover',
            '& code': { bgcolor: 'transparent', p: 0 },
          }}
        >
          {children}
        </Box>
      ),

      blockquote: ({ children }) => (
        <Box
          sx={{
            borderLeft: 2,
            borderColor: 'divider',
            pl: 2,
            my: 1.5,
            color: 'text.secondary',
          }}
        >
          {children}
        </Box>
      ),

      table: ({ children }) => (
        <Box sx={{ overflowX: 'auto', my: 1.5 }}>
          <Box
            component="table"
            sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem' }}
          >
            {children}
          </Box>
        </Box>
      ),
      th: ({ children }) => (
        <Box
          component="th"
          sx={{
            textAlign: 'left',
            px: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            color: 'text.secondary',
            fontWeight: 600,
          }}
        >
          {children}
        </Box>
      ),
      td: ({ children }) => (
        <Box
          component="td"
          sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
        >
          {children}
        </Box>
      ),

      hr: () => <Divider sx={{ my: 2 }} />,
    };

    // The entity element is not an intrinsic HTML tag, so it is merged in after
    // the typed map rather than declared inside it.
    return { ...standard, [ENTITY_ELEMENT]: EntityElement } as Components;
  }, [entities]);

  return (
    <Box>
      {aiGenerated && <AiMarker />}

      <ReactMarkdown remarkPlugins={remarkPlugins as never} components={components}>
        {source}
      </ReactMarkdown>

      {open && (
        <EntityMenu
          entity={open.entity}
          anchorEl={open.anchorEl}
          open
          onClose={() => setOpen(null)}
          actions={entityActions}
          onAction={onEntityAction}
        />
      )}
    </Box>
  );
}

function Heading({ level, children }: { level: 'h5' | 'h6' | 'subtitle1' | 'subtitle2'; children: React.ReactNode }) {
  return (
    <Typography variant={level} sx={{ mt: 2, mb: 1, '&:first-of-type': { mt: 0 } }}>
      {children}
    </Typography>
  );
}

/**
 * One marker for the whole generated block. Carbon's rules for an AI label
 * apply: it is the indicator, it must not be decorative, and it must not be an
 * action trigger -- so this is a static chip, not a button.
 */
function AiMarker() {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Chip
        purpose="category"
        size="small"
        label="AI generated"
        icon={<AutoAwesomeIcon />}
      />
    </Box>
  );
}

export default AnalystMarkdown;
